import type {
  AdminStatsResponse,
  AdminJobsOverviewResponse,
  AdminFailedJobsResponse,
  AdminFailedJobResponse,
  AdminRecurringTasksResponse,
  AdminScheduledJobsResponse,
  AdminPendingJobsResponse,
  AdminProcessingJobsResponse,
  AdminCompletedJobsResponse,
  AdminLogsResponse,
  AdminSessionsResponse,
  AdminApiUsageResponse,
} from '@/types'
import { request, buildQueryString } from './client'

export async function getAdminStats(): Promise<AdminStatsResponse> {
  return request<AdminStatsResponse>('/admin/stats')
}

export async function getAdminJobsOverview(): Promise<AdminJobsOverviewResponse> {
  return request<AdminJobsOverviewResponse>('/admin/jobs')
}

export async function getAdminFailedJobs(): Promise<AdminFailedJobsResponse> {
  return request<AdminFailedJobsResponse>('/admin/jobs/failed')
}

export async function getAdminFailedJob(id: number): Promise<AdminFailedJobResponse> {
  return request<AdminFailedJobResponse>(`/admin/jobs/failed/${id}`)
}

export async function retryAdminJob(id: number): Promise<{ message: string }> {
  return request(`/admin/jobs/failed/${id}/retry`, { method: 'POST' })
}

export async function discardAdminJob(id: number): Promise<{ message: string }> {
  return request(`/admin/jobs/failed/${id}`, { method: 'DELETE' })
}

export async function getAdminRecurringTasks(): Promise<AdminRecurringTasksResponse> {
  return request<AdminRecurringTasksResponse>('/admin/jobs/recurring')
}

export async function getAdminScheduledJobs(): Promise<AdminScheduledJobsResponse> {
  return request<AdminScheduledJobsResponse>('/admin/jobs/scheduled')
}

export async function cancelAdminScheduledJob(id: number): Promise<{ message: string }> {
  return request(`/admin/jobs/scheduled/${id}`, { method: 'DELETE' })
}

export async function cancelAllAdminScheduledJobs(jobClass?: string): Promise<{ message: string }> {
  const query = buildQueryString({ jobClass })
  return request(`/admin/jobs/scheduled${query}`, { method: 'DELETE' })
}

export async function getAdminPendingJobs(): Promise<AdminPendingJobsResponse> {
  return request<AdminPendingJobsResponse>('/admin/jobs/pending')
}

export async function getAdminProcessingJobs(): Promise<AdminProcessingJobsResponse> {
  return request<AdminProcessingJobsResponse>('/admin/jobs/processing')
}

export async function cancelAdminPendingJob(id: number): Promise<{ message: string }> {
  return request(`/admin/jobs/pending/${id}`, { method: 'DELETE' })
}

export async function cancelAllAdminPendingJobs(jobClass: string): Promise<{ message: string }> {
  const query = buildQueryString({ jobClass })
  return request(`/admin/jobs/pending${query}`, { method: 'DELETE' })
}

export async function getAdminCompletedJobs(jobClass?: string): Promise<AdminCompletedJobsResponse> {
  const query = buildQueryString({ jobClass })
  return request<AdminCompletedJobsResponse>(`/admin/jobs/completed${query}`)
}

export async function pauseAdminQueue(queueName: string): Promise<{ message: string }> {
  return request('/admin/jobs/pause', {
    method: 'POST',
    body: { queueName },
  })
}

export async function resumeAdminQueue(queueName: string): Promise<{ message: string }> {
  return request('/admin/jobs/resume', {
    method: 'POST',
    body: { queueName },
  })
}

export async function getAdminTriggerableJobs(): Promise<{ jobs: string[] }> {
  return request<{ jobs: string[] }>('/admin/jobs/triggerable')
}

export async function triggerAdminJob(jobClass: string): Promise<{ message: string }> {
  return request('/admin/jobs/trigger', {
    method: 'POST',
    body: { jobClass },
  })
}

export async function getAdminLogs(limit: number = 100): Promise<AdminLogsResponse> {
  const query = buildQueryString({ limit })
  return request<AdminLogsResponse>(`/admin/logs${query}`)
}

export async function getAdminSessions(): Promise<AdminSessionsResponse> {
  return request<AdminSessionsResponse>('/admin/sessions')
}

export async function revokeAdminSession(id: number): Promise<{ message: string }> {
  return request(`/admin/sessions/${id}`, { method: 'DELETE' })
}

export async function revokeUserSessions(userId: number): Promise<{ message: string }> {
  return request(`/admin/sessions/user/${userId}`, { method: 'DELETE' })
}

export async function getAdminApiUsage(): Promise<AdminApiUsageResponse> {
  return request<AdminApiUsageResponse>('/admin/api_usage')
}
